import { useState } from 'react';
import {
    CheckCircle,
    Trophy,
    Timer,
    Plus,
    Minus,
} from '@phosphor-icons/react';
import { STYLE_GUIDE } from '@/constants/testIds';

const initialSets = [
    { id: 1, weight: 60, reps: 10, state: 'completed' },
    { id: 2, weight: 65, reps: 10, state: 'completed' },
    { id: 3, weight: 70, reps: 8, state: 'pr' },
    { id: 4, weight: 70, reps: 6, state: 'rest' },
];

const stateChip = {
    completed: {
        label: 'DONE',
        cls:
            'bg-[#00FF9D]/15 text-[#00FF9D] border border-[#00FF9D]/30',
    },
    pr: {
        label: 'PR',
        cls: 'bg-[#FF4D4D]/15 text-[#FF4D4D] border border-[#FF4D4D]/30',
    },
    rest: {
        label: 'REST',
        cls:
            'bg-foreground/5 text-muted-foreground border border-foreground/10',
    },
};

export function ExerciseCardPreview() {
    const [sets, setSets] = useState(initialSets);
    const [pulsingId, setPulsingId] = useState(null);

    const completeSet = (id) => {
        setPulsingId(id);
        setSets((prev) =>
            prev.map((s) =>
                s.id === id && s.state === 'rest'
                    ? { ...s, state: 'completed' }
                    : s
            )
        );
        setTimeout(() => setPulsingId(null), 1200);
    };

    return (
        <div className="glass-card grain rounded-3xl p-6 sm:p-8 relative overflow-hidden">
            {/* Top row */}
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <p className="uppercase-mono text-muted-foreground mb-2">
                        Exercise · 03 / 06
                    </p>
                    <h3 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">
                        Barbell Bench Press
                    </h3>
                </div>
                <div className="shrink-0 grid place-items-center w-14 h-14 rounded-2xl bg-foreground/5 border border-foreground/10">
                    <span className="font-mono text-base font-bold">
                        4×
                    </span>
                </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat label="Working" value="70" unit="kg" />
                <Stat label="Total" value="2,160" unit="kg" highlight />
                <Stat label="Rest" value="01:45" unit="" mono />
            </div>

            {/* Sets list */}
            <div className="space-y-2.5">
                {sets.map((set, idx) => {
                    const chip = stateChip[set.state];
                    const isRest = set.state === 'rest';
                    const isPR = set.state === 'pr';
                    return (
                        <div
                            key={set.id}
                            className={`flex items-center gap-3 rounded-2xl border px-3 sm:px-4 py-3 transition-all ${
                                isPR
                                    ? 'border-[#FF4D4D]/40 bg-[#FF4D4D]/5'
                                    : isRest
                                      ? 'border-foreground/10 bg-foreground/[0.02] hover:bg-foreground/5'
                                      : 'border-[#00FF9D]/30 bg-[#00FF9D]/5'
                            }`}
                        >
                            <span className="font-mono text-xs text-muted-foreground w-6 tabular-nums">
                                {String(idx + 1).padStart(2, '0')}
                            </span>

                            <div className="flex-1 flex items-center gap-4">
                                <div className="flex items-baseline gap-1">
                                    <span className="font-mono text-xl font-bold tabular-nums">
                                        {set.weight}
                                    </span>
                                    <span className="uppercase-mono text-muted-foreground">
                                        kg
                                    </span>
                                </div>
                                <span className="text-muted-foreground">
                                    ×
                                </span>
                                <div className="flex items-baseline gap-1">
                                    <span className="font-mono text-xl font-bold tabular-nums">
                                        {set.reps}
                                    </span>
                                    <span className="uppercase-mono text-muted-foreground">
                                        reps
                                    </span>
                                </div>
                            </div>

                            <span
                                className={`uppercase-mono rounded-full px-2.5 py-1 ${chip.cls}`}
                            >
                                <span className="inline-flex items-center gap-1">
                                    {set.state === 'completed' && (
                                        <CheckCircle size={12} weight="fill" />
                                    )}
                                    {set.state === 'pr' && (
                                        <Trophy size={12} weight="fill" />
                                    )}
                                    {set.state === 'rest' && (
                                        <Timer size={12} weight="bold" />
                                    )}
                                    {chip.label}
                                </span>
                            </span>

                            {isRest && (
                                <button
                                    type="button"
                                    data-testid={`${STYLE_GUIDE.completeSetBtn}-${set.id}`}
                                    onClick={() => completeSet(set.id)}
                                    className={`relative grid place-items-center w-10 h-10 rounded-full bg-[#00FF9D] text-[#121212] hover:bg-[#00FF9D]/90 active:scale-[0.94] transition-all shadow-neon ${
                                        pulsingId === set.id
                                            ? 'animate-pulse-ring'
                                            : ''
                                    }`}
                                    aria-label="Mark set complete"
                                >
                                    <CheckCircle size={18} weight="fill" />
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Footer actions — thumb-friendly */}
            <div className="mt-6 grid grid-cols-[auto_1fr_auto] items-center gap-3">
                <button
                    type="button"
                    className="grid place-items-center w-12 h-12 rounded-2xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                    aria-label="Decrease weight"
                >
                    <Minus size={18} weight="bold" />
                </button>
                <button
                    type="button"
                    className="h-12 rounded-2xl bg-foreground text-background font-bold tracking-tight hover:opacity-90 active:scale-[0.99] transition-all"
                >
                    Log Next Set
                </button>
                <button
                    type="button"
                    className="grid place-items-center w-12 h-12 rounded-2xl border border-foreground/10 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.96] transition-all"
                    aria-label="Increase weight"
                >
                    <Plus size={18} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function Stat({ label, value, unit, highlight, mono }) {
    return (
        <div className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] px-3 py-3">
            <p className="uppercase-mono text-muted-foreground mb-1">
                {label}
            </p>
            <div className="flex items-baseline gap-1">
                <span
                    className={`font-mono font-bold tabular-nums ${
                        highlight
                            ? 'text-[#00FF9D] text-2xl'
                            : 'text-foreground text-xl'
                    } ${mono ? 'tracking-tight' : ''}`}
                >
                    {value}
                </span>
                {unit && (
                    <span className="uppercase-mono text-muted-foreground">
                        {unit}
                    </span>
                )}
            </div>
        </div>
    );
}
